/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";

import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";

// icons
import SaveIcon from "@material-ui/icons/Save";

// jaza-soft
import { PageFooter, PageHeader, Button, WithReduxForm, BackButton, translate } from "jazasoft";
import MuiTable from "../../../components/MuiTable";

// local
import CardHeader from "../../../components/CardHeader";

// helpers
import { isEmpty, listFromCsv } from "../../../utils/helpers";
import useCreateEdit from "./useCreateEdit";
import BasicDetails from "./BasicDetails";
import hasPrivilege from "../../../utils/hasPrivilege";
import Forbidden from "../../../components/Forbidden";
import { ResourceId, Scope } from "../../../utils/privilege";
import { OState, Role } from "../../../utils/types";
import { useEntityPrivilege } from "../../../hooks";

const stateValueMap = Object.values(OState).reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});

const sanitizeFlowInfo = ({
  productId,
  style,
  styleNo,
  color,
  fit,
  inseam,
  destination,
  delMode,
  exFactoryDate,
  orderQty,
  extra,
  shortShipPercent,
  flowRef,
  delFlowRef,
  customerFlowRef,
  gender,
  sleeveType,
  customField1,
  customField2,
  customField3
}) => ({
  productId,
  style,
  styleNo,
  color,
  fit,
  inseam,
  destination,
  delMode,
  exFactoryDate,
  orderQty,
  extra,
  shortShipPercent,
  flowRef,
  delFlowRef,
  customerFlowRef,
  gender,
  sleeveType,
  customField1,
  customField2,
  customField3
});

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3)
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "& > button": {
      marginRight: theme.spacing(2)
    }
  }
}));

const OrderEdit = props => {
  const { i18nKey, translate, roles, hasAccess } = props;
  const { disableEdit } = useEntityPrivilege("order");

  const classes = useStyles();

  const {
    orderFields,
    orderValidationRule,
    saving,
    order,
    columns,
    rows,
    allUnitList,
    orderCustomMultiFields,
    onSubmit,
    onValidate,
    onChange,
    showChildAddAction
  } = useCreateEdit(props);

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  let record = {
    ...order,
    ...orderCustomMultiFields.reduce((acc, field) => ({ ...acc, [field]: listFromCsv(order[field]) }), {})
  };
  const stateList = order?.flowInfoList?.flatMap(fi => fi.stateList || []) || [];
  // const isFabricAssociated = stateList.some(e => stateValueMap[e.state] > OState.NEW.stateValue);
  const isCutPlanInProgress = stateList.some(e => stateValueMap[e.state] > OState.FINAL_GROUPING_COMPLETED.stateValue);
  const isClosed = stateList.every(e => stateValueMap[e.state] === OState.CLOSED.stateValue);
  const isFPOCreated = allUnitList?.length > 1 && order?.flowInfoList?.some(e => !isEmpty(e.fpo));
  const isAdmin = roles?.includes(Role.ADMINISTRATOR) || roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER);

  if (!hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.WRITE) || disableEdit) {
    return <Forbidden history={props.history} />;
  }

  return (
    <div>
      {/** Header Element */}
      <PageHeader title="resources.orders.pageHeader.create" titleArgs={{ _: "Create Order" }} />

      {/* Main Content */}
      <div className={classes.content}>
        <WithReduxForm initialValues={record}>
          {({ handleSubmit }) => (
            <div>
              {/* Basic Details */}
              <BasicDetails
                className={classes.marginVertical}
                title={translate(`resources.orders.cardHeader.basicDetails`, { _: "Basic Details" })}
                fields={orderFields}
                validationRule={orderValidationRule}
                i18nKey={i18nKey}
                editing={true}
                isAdmin={isAdmin}
                isFPOCreated={isFPOCreated}
                isCutPlanInProgress={isCutPlanInProgress}
                isClosed={isClosed}
              />

              {/* Flow info */}
              <Card className={classes.marginVertical}>
                <CardHeader title={translate(`resources.orders.cardHeader.flowInfo`, { _: "Flow Info" })} />
                <Divider />
                <div>
                  <MuiTable
                    columns={columns}
                    rows={rows}
                    component="div"
                    editable
                    editing
                    showEditableActions
                    rowAddCount={1}
                    // onRowAdd={(rows, rowIdx) =>
                    //   !isEmpty(rowIdx)
                    //     ? { ...rows[rowIdx], id: null, partList: [], sizeBreakupList: [], ledgerList: [] }
                    //     : { ...rows[rows?.length - 1], id: null, partList: [], sizeBreakupList: [], ledgerList: [] }
                    // }
                    onRowAdd={(rows, rowIdx, flowInfo) => ({ ...sanitizeFlowInfo(flowInfo) })}
                    handleSubmitRef={handleSubmitRef}
                    onChange={onChange}
                    validate={onValidate}
                    onSubmit={onSubmit}
                    showChildAddAction={showChildAddAction}
                    fontSize={16}
                    idKey="key"
                    parentIdKey="parentKey"
                    i18nKey="resources.orders.fields.flowInfo"
                  />
                </div>
              </Card>

              {/* Footer */}
              <PageFooter className={classes.pageFooter}>
                <Button
                  label={translate("js.action.save", { _: "Save" })}
                  color="primary"
                  variant="contained"
                  disabled={false}
                  onClick={event => {
                    handleSubmit(() => {})();
                    ffHandleSubmit(event);
                  }}>
                  <SaveIcon />
                  {saving && <CircularProgress size={20} thickness={2} />}
                </Button>
                <BackButton label={translate("js.action.back", { _: "Back" })} variant="contained" />
              </PageFooter>
            </div>
          )}
        </WithReduxForm>
      </div>
    </div>
  );
};

export default translate(OrderEdit);
